import React, { useState, useEffect } from 'react';
import { 
  ClipboardList, 
  Search, 
  Building2, 
  ChevronRight, 
  ArrowLeft, 
  Calendar,
  Users,
  Printer,
  CheckCircle2,
  Star,
  Hash
} from 'lucide-react';
import { adminApiClient, Temple } from '../lib/api';
import { toast } from 'react-hot-toast';

interface SankalpamListItem {
  id: string;
  sourceId: string;
  sourceType: 'event' | 'sponsorship' | 'campaign';
  title: string;
  dateLabel: string;
  sortDate: string;
  frequency: 'once' | 'weekly' | 'monthly' | 'annual';
  isStarred: boolean;
  isPast: boolean;
}

interface Devotee {
  familyNames: string[];
  gothram: string;
  members: {
    name: string;
    nakshatra: string;
    rashi: string;
  }[];
}

export const SankalpamPage: React.FC = () => {
  const [selectedTemple, setSelectedTemple] = useState<Temple | null>(null);
  const [temples, setTemples] = useState<Temple[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Lists state
  const [lists, setLists] = useState<SankalpamListItem[]>([]);
  const [selectedList, setSelectedList] = useState<SankalpamListItem | null>(null);
  const [activeTab, setActiveTab] = useState<'current' | 'past'>('current');
  
  // Devotees state
  const [devotees, setDevotees] = useState<Devotee[]>([]);
  const [loadingDevotees, setLoadingDevotees] = useState(false);
  const [markedDevotees, setMarkedDevotees] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!selectedTemple) {
      fetchTemples();
    } else {
      fetchLists(selectedTemple._id);
    }
  }, [selectedTemple]);

  const fetchTemples = async () => {
    try {
      setLoading(true);
      const res = await adminApiClient.getTemples({ limit: 100 });
      if (res.success) {
        setTemples(res.data?.temples || []);
      }
    } catch (error) {
      toast.error('Failed to fetch temples');
    } finally {
      setLoading(false);
    }
  };

  const fetchLists = async (templeId: string) => {
    try {
      setLoading(true);
      const data = await adminApiClient.getSankalpamLists(templeId);
      if (data.success) {
        setLists(data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch Sankalpam lists');
    } finally {
      setLoading(false);
    }
  };

  const fetchDevotees = async (listId: string) => {
    try {
      setLoadingDevotees(true);
      const data = await adminApiClient.getSankalpamDevotees(listId);
      if (data.success) {
        setDevotees(data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch devotee list');
    } finally {
      setLoadingDevotees(false);
    }
  };

  const handleListClick = (item: SankalpamListItem) => {
    setSelectedList(item);
    fetchDevotees(item.id);
  };

  const toggleMark = (index: number) => {
    const newMarked = new Set(markedDevotees);
    if (newMarked.has(index)) newMarked.delete(index);
    else newMarked.add(index);
    setMarkedDevotees(newMarked);
  };

  const filteredTemples = temples.filter(t => 
    t.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredLists = lists.filter(l => {
    const matchesTab = activeTab === 'current' ? !l.isPast : l.isPast;
    return matchesTab;
  });

  // --- Rendering Functions ---

  if (selectedList) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <button 
            onClick={() => setSelectedList(null)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h2 className="text-xl font-bold text-gray-900">{selectedList.title}</h2>
            <p className="text-sm text-gray-500">{selectedList.dateLabel} &bull; {selectedTemple?.name}</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">{devotees.length} Devotees</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <span className="text-sm font-medium text-gray-700">{markedDevotees.size} Completed</span>
              </div>
            </div>
            <button 
               onClick={() => window.print()}
               className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-all"
            >
              <Printer className="w-4 h-4" />
              Print Mode
            </button>
          </div>

          <div className="p-6">
            {loadingDevotees ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => <div key={i} className="h-24 bg-gray-50 animate-pulse rounded-xl" />)}
              </div>
            ) : devotees.length > 0 ? (
              <div className="space-y-4">
                {devotees.map((devotee, idx) => (
                  <div 
                    key={idx}
                    onClick={() => toggleMark(idx)}
                    className={`p-5 rounded-xl border transition-all cursor-pointer ${
                      markedDevotees.has(idx) ? 'bg-green-50/30 border-green-200 opacity-70' : 'bg-white border-gray-100 shadow-sm hover:border-orange-200'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                        markedDevotees.has(idx) ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                      }`}>
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900">{devotee.familyNames.join(', ')}</h3>
                        <p className="text-sm text-gray-500 italic mt-1">Gothram: {devotee.gothram}</p>
                        <div className="mt-4 space-y-2">
                          {devotee.members.map((member, midx) => (
                            <div key={midx} className="flex items-center gap-6 text-sm text-gray-700 bg-gray-50 rounded-lg px-4 py-2">
                              <span className="font-bold min-w-[150px]">{member.name}</span>
                              <span className="flex items-center gap-1.5"><Star className="w-3.5 h-3.5 text-orange-400" />{member.nakshatra}</span>
                              <span className="flex items-center gap-1.5"><Hash className="w-3.5 h-3.5 text-blue-400" />{member.rashi}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-gray-200 mx-auto mb-4" />
                <p className="text-gray-500">No devotees registered for this ritual.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (selectedTemple) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setSelectedTemple(null)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{selectedTemple.name}</h1>
              <p className="text-sm text-gray-500">Sankalpam Lists & Ritual Preparation</p>
            </div>
          </div>
          <div className="flex bg-gray-100 p-1 rounded-xl">
            <button 
              onClick={() => setActiveTab('current')}
              className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'current' ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-500'}`}
            >
              Upcoming
            </button>
            <button 
              onClick={() => setActiveTab('past')}
              className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'past' ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-500'}`}
            >
              Past
            </button>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => <div key={i} className="h-40 bg-white animate-pulse rounded-2xl" />)}
          </div>
        ) : filteredLists.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredLists.map((item) => (
              <div 
                key={item.id}
                onClick={() => handleListClick(item)}
                className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-all cursor-pointer group"
              >
                <div className="flex justify-between items-start mb-4">
                  <span className="px-3 py-1 bg-gray-50 text-gray-600 text-[10px] font-bold uppercase rounded-full tracking-wider">
                    {item.sourceType}
                  </span>
                  {item.isStarred && <Star className="w-4 h-4 text-yellow-500 fill-current" />}
                </div>
                <h3 className="text-lg font-bold text-gray-900 group-hover:text-orange-600 transition-colors mb-2">{item.title}</h3>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Calendar className="w-4 h-4" />
                  {item.dateLabel}
                </div>
                <div className="mt-6 flex items-center justify-end text-orange-600 font-bold text-sm gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  View Names <ChevronRight className="w-4 h-4" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-20 text-center">
            <ClipboardList className="w-12 h-12 text-gray-200 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-gray-900">No ritual lists found</h3>
            <p className="text-gray-500">There are no rituals available for this temple in the selected period.</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Sankalpam Central</h1>
        <p className="text-gray-500 mt-1">Select a temple to view its ritual reading lists.</p>
      </div>

      <div className="mb-8 max-w-xl">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input 
            type="text"
            placeholder="Search by temple name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-6 py-4 bg-white border border-gray-200 rounded-2xl shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all text-lg"
          />
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-32 bg-white animate-pulse rounded-2xl" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemples.map((temple) => (
            <div 
              key={temple._id}
              onClick={() => setSelectedTemple(temple)}
              className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all cursor-pointer group flex items-center gap-4"
            >
              <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center text-orange-600 group-hover:bg-orange-600 group-hover:text-white transition-all">
                <Building2 className="w-6 h-6" />
              </div>
              <div className="flex-1 overflow-hidden">
                <h3 className="font-bold text-gray-900 truncate">{temple.name}</h3>
                <p className="text-xs text-gray-500 truncate">{temple.address.city}, {temple.address.state}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-orange-600" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
